/**
 * Slice 15 — curated MRG starter catalog for VA property inventory setup.
 * Not furniture/fixtures (those belong in Inspect).
 */
export type CatalogTrack = "units" | "fullness";

export type CatalogCategory = "Kitchen" | "Bath" | "Linens" | "Cleaning" | "Other";

export type CatalogItem = {
  name: string;
  /** Default track suggestion — VA can change on the Add sheet */
  track: CatalogTrack;
  category: CatalogCategory;
};

export const INVENTORY_CATALOG_CATEGORIES: Array<CatalogCategory | "All"> = [
  "All",
  "Kitchen",
  "Bath",
  "Linens",
  "Cleaning",
  "Other",
];

export const INVENTORY_CATALOG: CatalogItem[] = [
  // Kitchen
  { name: "Cups", track: "units", category: "Kitchen" },
  { name: "Big plates", track: "units", category: "Kitchen" },
  { name: "Small plates", track: "units", category: "Kitchen" },
  { name: "Utensils", track: "units", category: "Kitchen" },
  { name: "Dish towel", track: "units", category: "Kitchen" },
  { name: "Garbage bags", track: "fullness", category: "Kitchen" },
  { name: "Dishwasher pods", track: "units", category: "Kitchen" },
  { name: "Sponges", track: "units", category: "Kitchen" },
  { name: "Dish soap", track: "fullness", category: "Kitchen" },
  { name: "Paper towels", track: "units", category: "Kitchen" },
  // Bath
  { name: "Toilet paper", track: "units", category: "Bath" },
  { name: "Bath towels", track: "units", category: "Bath" },
  { name: "Hand towels", track: "units", category: "Bath" },
  { name: "Bath mat", track: "units", category: "Bath" },
  { name: "Toilet cleaner", track: "fullness", category: "Bath" },
  { name: "Plunger", track: "units", category: "Bath" },
  // Linens
  { name: "Queen sheet set", track: "units", category: "Linens" },
  { name: "Queen duvet cover", track: "units", category: "Linens" },
  { name: "Pillowcases", track: "units", category: "Linens" },
  { name: "Mattress protector", track: "units", category: "Linens" },
  // Cleaning
  { name: "All-purpose cleaner", track: "fullness", category: "Cleaning" },
  { name: "Windex", track: "fullness", category: "Cleaning" },
  { name: "Floor cleaner", track: "fullness", category: "Cleaning" },
  { name: "Laundry detergent", track: "fullness", category: "Cleaning" },
  { name: "Mop & bucket", track: "units", category: "Cleaning" },
  { name: "Lysol", track: "fullness", category: "Cleaning" },
  { name: "Disinfectant wipes", track: "units", category: "Cleaning" },
  { name: "Febreze", track: "fullness", category: "Cleaning" },
  { name: "Lint roller", track: "units", category: "Cleaning" },
  // Other
  { name: "Batteries · AA", track: "units", category: "Other" },
  { name: "Light bulbs", track: "units", category: "Other" },
  { name: "Coffee pods", track: "units", category: "Other" },
  { name: "Tea bags", track: "units", category: "Other" },
  { name: "Air freshener refill", track: "fullness", category: "Other" },
];

export function trackLabel(track: CatalogTrack): "Count" | "Level" {
  return track === "fullness" ? "Level" : "Count";
}

/** Level chips — Slice 15 copy lock */
export const LEVEL_NOW_OPTIONS = [
  { value: 1.0, label: "Full" },
  { value: 0.75, label: "¾" },
  { value: 0.5, label: "½" },
  { value: 0.25, label: "¼" },
  { value: 0.0, label: "Empty" },
] as const;

export const LEVEL_LOW_OPTIONS = [
  { value: 0.75, label: "≤ ¾" },
  { value: 0.5, label: "≤ ½" },
  { value: 0.25, label: "≤ ¼" },
  { value: 0.0, label: "Empty" },
] as const;

export function formatLevelNow(value: number | null | undefined): string {
  if (value == null) return "—";
  const hit = LEVEL_NOW_OPTIONS.find((o) => o.value === value);
  if (hit) return hit.label;
  const nearest = LEVEL_NOW_OPTIONS.reduce((best, o) =>
    Math.abs(o.value - value) < Math.abs(best.value - value) ? o : best,
  );
  return nearest.label;
}

export function formatLevelLowAt(value: number | null | undefined): string {
  if (value == null) return "—";
  const hit = LEVEL_LOW_OPTIONS.find((o) => o.value === value);
  if (hit) return hit.label;
  return `≤ ${formatLevelNow(value)}`;
}
